import { Component, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { resolveClientRefDisplayColor } from "@app/_helpers/client-color";
import formatByLocale from "@app/_helpers/date-fns/format";
import { useUserFind } from "@app/_helpers/useUtils";
import { createDateRangeArray, createRxValue, DateRangeType, getSeriesTypeDateBounds } from "@app/_helpers/utils";
import { UtilService } from "@app/_services/util.service";
import { AppService } from "@app/app.service";
import { ItemBillingFlag } from "@app/shared/filters/item-billing-flag";
import { TIME_RANGE_CUSTOM_NAME, TRange } from "@app/shared/time-range-picker/time-range-constants";
import { TimeTrackerCalendarUpdateDialogComponent } from "@app/shared/time-tracker-calendar-update-dialog/time-tracker-calendar-update-dialog.component";
import { TranslateService } from "@ngx-translate/core";
import { ClientsQuery, Logger, ProjectsQuery, Time, UserSettingsQuery } from "@timeghost/web-api";
import { ApexOptions } from "apexcharts";
import { CalendarEvent } from "calendar-utils";
import { roundToNearestMinutes } from "date-fns/esm";
import { merge } from "lodash-es";
import { combineLatest } from "rxjs";
import { distinctUntilChanged, map, shareReplay, tap } from "rxjs/operators";
import { GroupType } from "../data-table/data-table.component";
import { ReportsService } from "../reports.service";
import { aggregateDurationsBySeriesBuckets } from "../reports-chart-series";
import { ReportItem, useTransformReportItemToTime } from "../utils";

const log = new Logger("SummaryPageComponent");
type SummaryPageItem = ReportItem<Time>;
@Component({
	selector: "tg-summary-page",
	templateUrl: "./summary-page.component.html",
	styleUrls: ["./summary-page.component.scss"],
})
export class SummaryPageComponent implements OnInit {
	constructor(
		private reports: ReportsService,
		private userSettingsQuery: UserSettingsQuery,
		private translate: TranslateService,
		private dialog: MatDialog,
		private projectsQuery: ProjectsQuery,
		private clientsQuery: ClientsQuery,
		private appService: AppService
	) {}

	readonly graphDisplayByProject = new FormControl(false);

	ngOnInit(): void {
		this.reports.lastContingentUrl.update(null);
	}

	get group() {
		return this.reports.groupBy.value;
	}
	set group(val: GroupType) {
		this.reports.groupBy.next(val);
	}
	get range() {
		return this.reports.range.value;
	}
	set range(val: TRange) {
		this.drillHistory$.update([]);
		this.reports.onRangeUpdate(val);
	}
	readonly showAmounts$ = this.reports.showAmounts$;
	readonly timeLoading$ = this.reports.timeLoading$;
	readonly transformReportItemToTime = useTransformReportItemToTime();
	readonly roundedTimes$ = combineLatest([
		this.reports.data$filtered.asObservable(),
		this.reports.roundingData$,
	]).pipe(
		this.reports.roundingHandler(),
		map(([times]) => times.map(this.transformRecordingTime)),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	readonly data$table = this.roundedTimes$.pipe(
		map((times) => times.map(this.transformReportItemToTime)),
		tap(() => log.debug("Data$Table")),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	readonly data$stats = this.reports.data$stats;

	readonly drillHistory$ = createRxValue<TRange[]>([]);
	readonly canDrillBack$ = this.drillHistory$.value$.pipe(map((h) => h.length > 0));

	drillBack() {
		const history = this.drillHistory$.value;
		if (!history.length) return;
		const prev = history[history.length - 1];
		this.drillHistory$.update(history.slice(0, -1));
		this.reports.onRangeUpdate(prev);
	}

	private _drillRange(val: TRange) {
		this.drillHistory$.update([...this.drillHistory$.value, this.range]);
		this.reports.onRangeUpdate(val);
	}

	readonly graphDrillable$ = this.reports.range$.pipe(
		map((range) => {
			const [, seriesType] = createDateRangeArray(range.from, range.to, true);
			return seriesType === "week" || seriesType === "month";
		}),
		distinctUntilChanged(),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	private buildDrillClickHandler(seriesType: DateRangeType, seriesRange: Array<[Date, any]>) {
		return (_event: MouseEvent, _chartContext: unknown, config: { dataPointIndex?: number }) => {
			if (seriesType !== "week" && seriesType !== "month") return;
			const index = config?.dataPointIndex;
			if (index == null || index < 0) return;
			const date = seriesRange[index]?.[0];
			if (!date) return;
			const bounds = getSeriesTypeDateBounds(seriesType, date);
			if (!bounds) return;
			// clamp to the outer range so a partial first/last week stays within the selected month
			const outerRange = this.reports.range.value;
			const from = bounds.from < outerRange.from ? outerRange.from : bounds.from;
			const to = bounds.to > outerRange.to ? outerRange.to : bounds.to;
			this._drillRange({ name: TIME_RANGE_CUSTOM_NAME, rangeType: null, from, to });
		};
	}

	readonly data$graph = combineLatest([this.roundedTimes$, this.reports.range$]).pipe(
		map(([_times, range]): ApexOptions => {
			const [seriesRange, seriesType] = createDateRangeArray<number>(range.from, range.to, true);
			const options = <Partial<ApexOptions>>{
				chart: {
					type: "bar",
					stackType: "normal",
					stacked: true,
					events: {
						click: this.buildDrillClickHandler(seriesType, seriesRange),
					},
				},
				series: [
					{
						data: aggregateDurationsBySeriesBuckets(
							_times,
							seriesType,
							seriesRange,
							(x) => !(x as SummaryPageItem).billable
						),
						name: `${this.translate.instant("dashboard.nonBilled")}`,
						color: "#2196f3",
						hidden: this.reports.billableFilter.value === ItemBillingFlag.BILLED,
					},
					{
						data: aggregateDurationsBySeriesBuckets(
							_times,
							seriesType,
							seriesRange,
							(x) => !!(x as SummaryPageItem).billable
						),
						name: `${this.translate.instant("dashboard.billed")}`,
						color: "#e5ad32",
						hidden: this.reports.billableFilter.value === ItemBillingFlag.NOT_BILLED,
					},
				],
				xaxis: {
					categories: seriesRange.map(([key]) => key),
				},
			};
			const formatYAxis = (v: number): any => {
				if (v === 0.0) return undefined;
				return UtilService.parseMS(v * 1000, { showNegative: false, showSeconds: false });
			};
			return merge(options, <Partial<ApexOptions>>{
				yaxis: {
					labels: {
						formatter: formatYAxis,
					},
				},
				xaxis: {
					labels: {
						formatter: (v: any) => {
							return v
								? formatByLocale(
										v,
										seriesType === "day"
											? `MMM'\n'do`
											: seriesType === "week"
												? `MMM'\nWeek 'I`
												: seriesType === "month"
													? `MMM`
													: null,
										{
											weekStartsOn: 1,
											locale: this.userSettingsQuery.getValue().settings.languageSetting,
										}
									).split("\n")
								: "-";
						},
					},
				},
			});
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly times$ = this.roundedTimes$;
	readonly findUser = useUserFind();
	readonly data$byGroupType = combineLatest([
		this.times$,
		this.reports.groupBy.asObservable(),
		this.reports.range$,
	]).pipe(
		map(([_times, groupType, range]): ApexOptions => {
			const [seriesRange, seriesType] = createDateRangeArray<number>(range.from, range.to, true);
			let timesByGroupType: { data: number[]; name: string; color: string }[] = [];
			// groupd times by groupType
			const groupedByProject = _times.reduce(
				(acc, item) => {
					let key;
					if (groupType === GroupType.Client) key = item.clientId;
					else if (groupType === GroupType.Project) key = item.projectId;
					else if (groupType === GroupType.Task) key = item.taskId ?? "no task";
					else if (groupType === GroupType.User) key = item.userId;

					if (!acc[key]) {
						acc[key] = [];
					}
					acc[key].push(item);
					return acc;
				},
				{} as Record<string, typeof _times>
			);
			// create the data for the graph
			timesByGroupType = Object.entries(groupedByProject)
				.map(([key, items]: [string, SummaryPageItem[]]) => {
					// default for name and colro
					let name = "";
					let color;
					const base = items.length > 0 ? items[0] : null;
					if (groupType === GroupType.Client) {
						name =
							base.clientName === "No Client" ? this.translate.instant("client.none") : base.clientName;
						color = this.clientGraphColor(key);
					} else if (groupType === GroupType.Project) {
						name =
							base.projectName === "No Project"
								? this.translate.instant("project.none")
								: base.projectName;
						color = this.projectsQuery.getEntity(key)?.color ?? "#2196f3";
					} else if (groupType === GroupType.Task) {
						name = base.taskName ?? this.translate.instant("task.none");
					} else if (groupType === GroupType.User) {
						name = this.findUser(base.userId)?.name ?? base.userId;
					}

					if (!color) color = this.generateColorFromName(name);

					const data = aggregateDurationsBySeriesBuckets(items, seriesType, seriesRange);

					return { data, name, color };
				})
				.sort((a, b) => {
					const dataSumA = a.data.reduce((sum, value) => {
						sum += value;
						return sum;
					}, 0);
					const dataSumB = b.data.reduce((sum, value) => {
						sum += value;
						return sum;
					}, 0);
					return dataSumB - dataSumA;
				})
				.slice(0, 10);

			const options = <Partial<ApexOptions>>{
				chart: {
					type: "bar",
					stackType: "normal",
					stacked: true,
					events: {
						click: this.buildDrillClickHandler(seriesType, seriesRange),
					},
				},
				series: timesByGroupType,
				xaxis: {
					categories: seriesRange.map(([key]) => key),
				},
			};
			const user = this.userSettingsQuery.getValue();
			const formatYAxis = (v: number): any => {
				if (v === 0.0) return undefined;
				return UtilService.parseMS(v * 1000, { showNegative: false, showSeconds: false });
			};
			return merge(options, <Partial<ApexOptions>>{
				yaxis: {
					labels: {
						formatter: formatYAxis,
					},
				},
				xaxis: {
					labels: {
						formatter: (v: any) => {
							return v
								? formatByLocale(
										v,
										seriesType === "day"
											? `MMM'\n'do`
											: seriesType === "week"
												? `MMM'\nWeek 'I`
												: seriesType === "month"
													? `MMM`
													: null,
										{
											weekStartsOn: 1,
											locale: this.userSettingsQuery.getValue().settings.languageSetting,
										}
									).split("\n")
								: "-";
						},
					},
				},
			});
		})
	);
	readonly graph$ = combineLatest([
		this.data$graph,
		this.data$byGroupType,
		this.reports.groupBy.asObservable().pipe(distinctUntilChanged()),
	]).pipe(
		map(([graph, byGroupType, groupType]) => {
			if (groupType !== GroupType.Billable) return byGroupType;
			return graph;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	onNameClick({ entity }: { group: GroupType; entity: SummaryPageItem }) {
		if (!entity.currentUserCanEdit) return;
		return this.dialog
			.open(TimeTrackerCalendarUpdateDialogComponent, {
				data: <CalendarEvent>{
					title: entity.name,
					start: new Date(entity.start),
					end: new Date(entity.end),
					meta: { time: entity },
				},
			})
			.afterClosed()
			.subscribe();
	}
	onDeleteTime(time: SummaryPageItem) {
		this.reports.projecthours.update((state) => state.filter((x) => x.id !== time.id), true);
	}
	onRestoreTime() {
		this.reports.reload({ silent: true });
	}
	clientGraphColor(clientId: string) {
		return resolveClientRefDisplayColor(
			{ id: clientId },
			(id) => this.clientsQuery.getEntity(id),
			(n) => this.appService.colorScheme.getColorFromScheme(n)
		);
	}

	generateColorFromName(name: string): string {
		let hash = 0;
		for (let i = 0; i < name.length; i++) {
			hash = name.charCodeAt(i) + ((hash << 5) - hash);
		}

		// Convert hash to RGB
		const r = (hash >> 0) & 0xff;
		const g = (hash >> 8) & 0xff;
		const b = (hash >> 16) & 0xff;

		return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
	}

	transformRecordingTime(time: any) {
		if (!time.recording) return time;
		const now = roundToNearestMinutes(new Date(), { nearestTo: 1 });
		return {
			...time,
			duration: (now.getTime() - time.start.getTime()) / 1000,
			timeDiff: (now.getTime() - time.start.getTime()) / 1000,
		};
	}
}
